(() => {
  if (customElements.get('evolt-comparison')) return;
  class EvoltComparison extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.q = (name) => this.querySelector(`[data-${name}]`);
      const on = (node, type, fn, options = {}) => node.addEventListener(type, fn, { ...options, signal: this.controller.signal });
      try {
        this.config = JSON.parse(this.q('config').textContent);
        this.data = JSON.parse(this.q('products-data').textContent);
      } catch (_) {
        this.q('empty').textContent = this.config?.text?.error || 'Primerjava trenutno ni na voljo.';
        return;
      }
      this.t = this.config.text;
      this.max = Number(this.dataset.max);
      this.initial = this.unique(this.data.initialProducts || []).slice(0, this.max);
      this.products = this.unique([...(this.data.products || []), ...this.initial]);
      this.byId = new Map(this.products.map(p => [String(p.id), p]));
      this.searchItems = this.products.map(p => ({ p, term: this.normalize(p.title) }));
      this.rows = this.config.keys.map((key, i) => ({ key: key.trim(), label: (this.config.labels[i] || '').trim() })).filter(r => r.key && r.label);
      this.selected = [...this.initial];
      this.expanded = false;
      this.replaceId = null;
      this.resultLimit = 12;
      this.q('tools').hidden = false;
      on(this, 'click', event => {
        const b = event.target.closest('button');
        if (!b || !this.contains(b)) return;
        if (b.hasAttribute('data-picker-toggle')) this.togglePicker(this.q('picker-toggle').getAttribute('aria-expanded') !== 'true');
        if (b.hasAttribute('data-close')) this.togglePicker(false);
        if (b.dataset.remove) this.remove(b.dataset.remove);
        if (b.dataset.replace) { this.replaceId = b.dataset.replace; this.togglePicker(true); }
        if (b.dataset.add) this.add(b.dataset.add);
        if (b.hasAttribute('data-reset')) {
          this.selected = [...this.initial]; this.expanded = false;
          this.togglePicker(false); this.render(); this.announce(this.t.restored); this.q('reset').focus();
        }
        if (b.hasAttribute('data-expand')) { this.expanded = !this.expanded; this.renderRows(); this.q('expand').focus(); }
        if (b.hasAttribute('data-more-results')) { this.resultLimit += 12; this.renderResults(); }
        if (b.hasAttribute('data-prev') || b.hasAttribute('data-next')) {
          const width = this.q('head').children[1]?.getBoundingClientRect().width || 200;
          this.q('scroll').scrollBy({ left: width * (b.hasAttribute('data-next') ? 1 : -1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      });
      on(this.q('search'), 'input', () => { this.resultLimit = 12; this.renderResults(); });
      on(this.q('picker'), 'keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); this.togglePicker(false); return; }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const buttons = [...this.q('results').querySelectorAll('button:not(:disabled)')];
        if (!buttons.length) return;
        const i = buttons.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? Math.min(i + 1, buttons.length - 1) : Math.max(0, i - 1);
        event.preventDefault(); buttons[next].focus();
      });
      on(this.q('scroll'), 'scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = null; this.updateNavigation(); });
      }, { passive: true });
      this.resize = new ResizeObserver(() => this.updateNavigation());
      this.resize.observe(this.q('scroll'));
      this.render();
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.resize?.disconnect(); if (this.frame) cancelAnimationFrame(this.frame); }
    unique(products) { return [...new Map(products.filter(p => p && p.id).map(p => [String(p.id), p])).values()]; }
    normalize(value) { return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase(); }
    node(tag, className, text) { const n = document.createElement(tag); if (className) n.className = className; if (text !== undefined) n.textContent = text; return n; }
    button(text, attr, value = '') { const n = this.node('button', '', text); n.type = 'button'; n.setAttribute(`data-${attr}`, value); return n; }
    safeURL(value) { try { const u = new URL(value, location.origin); return ['http:', 'https:'].includes(u.protocol) ? u.href : ''; } catch (_) { return ''; } }
    image(p, className, size) {
      const img = this.node('img', className); const url = this.safeURL(p.featured_image);
      if (!url || !p.featured_image) return this.node('span', className);
      img.src = url; img.alt = ''; img.loading = 'lazy'; img.decoding = 'async'; img.width = size; img.height = size; return img;
    }
    announce(text) { this.q('status').textContent = text; }
    togglePicker(open) {
      this.q('picker').hidden = !open; this.q('picker-toggle').setAttribute('aria-expanded', String(open));
      if (open) {
        this.q('search').value = ''; this.resultLimit = 12; this.renderResults();
        this.announce(this.replaceId ? `${this.t.replacing} ${this.byId.get(this.replaceId)?.title || ''}` : this.t.choose_hint);
        this.q('search').focus({ preventScroll: true }); this.q('picker').scrollIntoView({ block: 'nearest', behavior: 'instant' });
      } else { this.replaceId = null; this.q('picker-toggle').focus({ preventScroll: true }); this.announce(''); }
    }
    renderResults() {
      const words = this.normalize(this.q('search').value.trim()).split(/\s+/).filter(Boolean);
      const selected = new Set(this.selected.map(p => String(p.id)));
      const matches = this.searchItems.filter(item => !selected.has(String(item.p.id)) && words.every(w => item.term.includes(w)));
      const full = this.selected.length >= this.max && !this.replaceId;
      this.q('result-count').textContent = full ? this.t.limit.replace('{max}', this.max) : matches.length ? `${matches.length} ${this.t.results}` : this.t.no_results;
      const fragment = document.createDocumentFragment();
      matches.slice(0, this.resultLimit).forEach(({ p }) => {
        const b = this.button('', 'add', p.id); b.className = 'ev-cg__result'; b.disabled = full;
        b.append(this.image(p, '', 48));
        const info = this.node('span', '', p.title); info.append(this.node('strong', '', p.price_formatted)); b.append(info); fragment.append(b);
      });
      this.q('results').replaceChildren(fragment);
      this.q('more-results').hidden = matches.length <= this.resultLimit;
    }
    add(id) {
      const p = this.byId.get(String(id)); if (!p || this.selected.some(s => String(s.id) === String(id))) return;
      const index = this.selected.findIndex(s => String(s.id) === this.replaceId);
      if (index >= 0) this.selected.splice(index, 1, p);
      else if (this.selected.length < this.max) this.selected.push(p);
      else { this.announce(this.t.limit.replace('{max}', this.max)); return; }
      this.togglePicker(false); this.render(); this.announce(`${this.t.added} ${p.title}`);
    }
    remove(id) {
      this.selected = this.selected.filter(p => String(p.id) !== id); this.replaceId = null; this.render();
      if (!this.q('picker').hidden) this.renderResults();
      this.q('picker-toggle').focus({ preventScroll: true }); this.announce(this.t.removed);
    }
    render() {
      const has = this.selected.length > 0;
      this.q('scroll').hidden = !has; this.q('empty').hidden = has;
      this.q('empty').textContent = this.config.empty;
      this.q('count').textContent = `${this.selected.length} / ${this.max} ${this.t.products}`;
      this.q('table').style.setProperty('--cg-count', Math.max(1, this.selected.length));
      while (this.q('head').children.length > 1) this.q('head').lastElementChild.remove();
      const fragment = document.createDocumentFragment();
      this.selected.forEach(p => {
        const cell = this.node('th'); cell.scope = 'col';
        if (String(p.id) === this.dataset.base) cell.setAttribute('data-current', '');
        const top = this.node('div', 'ev-cg__card-top');
        top.append(this.node('span', 'ev-cg__badge', String(p.id) === this.dataset.base ? this.t.current : this.t.compare));
        const remove = this.button('×', 'remove', p.id); remove.className = 'ev-cg__remove'; remove.setAttribute('aria-label', `${this.t.remove} ${p.title}`); top.append(remove); cell.append(top);
        cell.append(this.image(p, 'ev-cg__image', 104));
        const title = this.node('a', 'ev-cg__product-title', p.title); title.href = this.safeURL(p.url); cell.append(title);
        cell.append(this.node('div', 'ev-cg__price', p.price_formatted));
        const view = this.node('a', 'ev-cg__button ev-cg__view', this.config.view); view.href = this.safeURL(p.url); view.setAttribute('aria-label', `${this.config.view}: ${p.title}`); const actions = this.node('div', 'ev-cg__actions'); actions.append(view);
        const replace = this.button(this.t.replace, 'replace', p.id); replace.className = 'ev-cg__text-button ev-cg__replace'; replace.setAttribute('aria-label', `${this.t.replace}: ${p.title}`); actions.append(replace); cell.append(actions); fragment.append(cell);
      });
      this.q('head').append(fragment); this.renderRows(); this.updateNavigation();
    }
    value(value) {
      if (value === null || value === undefined || value === '' || value === 'N/A') return this.config.emptyValue || '—';
      if (value === true || value === 'true') return this.t.yes;
      if (value === false || value === 'false') return this.t.no;
      if (Array.isArray(value)) return value.map(v => this.value(v)).join(', ');
      if (typeof value === 'object') {
        if ('value' in value && 'unit' in value) return `${value.value} ${value.unit}`;
        if (Array.isArray(value.children)) return value.children.map(v => this.value(v)).join(value.type === 'root' ? '\n' : '');
        if ('value' in value) return String(value.value);
        return Object.values(value).map(v => this.value(v)).join(' · ');
      }
      return String(value);
    }
    renderRows() {
      const matching = this.rows;
      const shown = this.expanded ? matching : matching.slice(0, 4);
      const fragment = document.createDocumentFragment();
      shown.forEach(row => {
        const tr = this.node('tr'); const label = this.node('th', '', row.label); label.scope = 'row'; tr.append(label);
        this.selected.forEach(p => {
          const td = this.node('td'); td.dataset.metafield = row.key;
          if (String(p.id) === this.dataset.base) td.setAttribute('data-current', '');
          const value = this.value(p.metafields?.[row.key]);
          if (/^https?:\/\//i.test(value) && this.safeURL(value)) {
            const a = this.node('a', 'ev-cg__value-link', this.config.link); a.href = this.safeURL(value); a.target = '_blank'; a.rel = 'noopener'; td.append(a);
          } else {
            if (/^#(?:[a-f\d]{3}|[a-f\d]{6})$/i.test(value)) { const swatch = this.node('span', 'ev-cg__swatch'); swatch.style.backgroundColor = value; swatch.setAttribute('aria-hidden', 'true'); td.append(swatch); }
            td.append(document.createTextNode(value));
          }
          tr.append(td);
        }); fragment.append(tr);
      });
      this.q('body').replaceChildren(fragment);
      this.toggleAttribute('data-expanded', this.expanded);
      const expand = this.q('expand'); expand.hidden = !this.selected.length || matching.length <= 4;
      expand.textContent = this.expanded ? this.config.less : `${this.config.more} (${matching.length})`;
      expand.setAttribute('aria-expanded', String(this.expanded));
      this.announce('');
      this.updateNavigation();
    }
    updateNavigation() {
      const el = this.q('scroll'); const overflow = !el.hidden && el.scrollWidth > el.clientWidth + 2;
      this.q('navigation').hidden = !overflow;
      this.q('prev').disabled = el.scrollLeft < 2;
      this.q('next').disabled = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    }
  }
  customElements.define('evolt-comparison', EvoltComparison);
})();
